import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, ChevronDown, FileText, ClipboardPlus, UserPlus, Users, Smile, Layers, Wrench, Fingerprint, UserSearch, BookOpen, Dices, FileCode2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { trackButton } from "@/lib/analytics";

const navLinks = [
  { label: "Contact Sales", to: "/contact-sales", testid: "nav-contact" },
];

const demoItems = [
  { label: "HCFA / CMS-1500", desc: "Professional claim demo", icon: FileText, to: "/demo/hcfa-cms-1500" },
  { label: "UB-04 / CMS-1450", desc: "Institutional claim demo", icon: ClipboardPlus, to: "/demo/ub-04-cms-1450" },
  { label: "ADA J430D Dental", desc: "Dental claim demo", icon: Smile, to: "/demo/ada-dental" },
  { label: "Insurance Enrollment", desc: "Enrollment intake demo", icon: UserPlus, to: "/demo/insurance-enrollment" },
];

const productItems = [
  { label: "Form OCR", desc: "Any business form → structured JSON", icon: Layers, to: "/form-ocr" },
  { label: "CMS 1500 OCR", desc: "HCFA professional claims", icon: FileText, to: "/cms-1500-ocr" },
  { label: "CMS 1450 OCR", desc: "UB-04 institutional claims", icon: ClipboardPlus, to: "/cms-1450-ocr" },
  { label: "ADA J430D OCR", desc: "Dental claim forms", icon: Smile, to: "/ada-j430d-ocr" },
  { label: "Enrollment Form OCR", desc: "Insurance enrollment to 834 EDI", icon: UserPlus, to: "/enrollment-form-ocr" },
];

const toolItems = [
  { label: "All Tools", desc: "Free NPI toolkit overview", icon: Wrench, to: "/tools" },
  { label: "NPI Validator", desc: "Free NPI checksum & NPPES lookup", icon: Fingerprint, to: "/tools/npi-validator" },
  { label: "Fake NPI Generator", desc: "Checksum-valid test NPI numbers", icon: Dices, to: "/tools/fake-npi-generator" },
  { label: "Fake Patient Generator", desc: "Synthetic patient test records", icon: Users, to: "/tools/fake-patient-generator" },
  { label: "Synthetic 837 Generator", desc: "Payer-specific 837 test files", icon: FileCode2, to: "/tools/payer-specific-synthetic-837" },
  { label: "NPI Lookup by Name", desc: "Find a provider or org's NPI", icon: UserSearch, to: "/tools/npi-lookup-by-name" },
];

const resourceItems = [
  { label: "CMS-1500 Claim Form boxes list", desc: "Every box explained + 837P mapping", icon: BookOpen, to: "/resources/cms-1500-claim-form-boxes-list" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-brand ${
      isActive ? "text-brand" : "text-slate-600"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70 shadow-sm"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="header-logo-link" className="flex items-center">
          <img src="/logo.png" alt="Hewto.ai" className="h-8 w-auto" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-products-dropdown"
              className="flex items-center gap-1 text-sm font-medium text-slate-600 outline-none transition-colors hover:text-brand"
            >
              Products <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 p-2">
              {productItems.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  data-testid={`product-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => navigate(item.to)}
                  className="flex cursor-pointer items-start gap-3 rounded-lg p-3"
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand">
                    <item.icon className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{item.label}</span>
                    <span className="block text-xs text-slate-500">{item.desc}</span>
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-demo-dropdown"
              className="flex items-center gap-1 text-sm font-medium text-slate-600 outline-none transition-colors hover:text-brand"
            >
              Demo <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 p-2">
              {demoItems.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  data-testid={`demo-item-${item.to.split("/").pop()}`}
                  onClick={() => navigate(item.to)}
                  className="flex cursor-pointer items-start gap-3 rounded-lg p-3"
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand">
                    <item.icon className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{item.label}</span>
                    <span className="block text-xs text-slate-500">{item.desc}</span>
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-tools-dropdown"
              className="flex items-center gap-1 text-sm font-medium text-slate-600 outline-none transition-colors hover:text-brand"
            >
              Tools <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 p-2">
              {toolItems.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  data-testid={`tool-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => navigate(item.to)}
                  className="flex cursor-pointer items-start gap-3 rounded-lg p-3"
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand">
                    <item.icon className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{item.label}</span>
                    <span className="block text-xs text-slate-500">{item.desc}</span>
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-resources-dropdown"
              className="flex items-center gap-1 text-sm font-medium text-slate-600 outline-none transition-colors hover:text-brand"
            >
              Resources <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 p-2">
              {resourceItems.map((item) => (
                <DropdownMenuItem
                  key={item.label}
                  data-testid={`resource-item-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => navigate(item.to)}
                  className="flex cursor-pointer items-start gap-3 rounded-lg p-3"
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand">
                    <item.icon className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{item.label}</span>
                    <span className="block text-xs text-slate-500">{item.desc}</span>
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {navLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.testid} className={linkClass}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Button
            data-testid="header-book-demo-btn"
            onClick={() => {
              trackButton("Book Demo", { source: "header" });
              navigate("/contact-sales");
            }}
            className="rounded-full bg-brand px-5 font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-600"
          >
            Book a Demo
          </Button>
        </div>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-trigger"
                className="text-slate-700"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <div className="mt-4 mb-8">
                <img src="/logo.png" alt="Hewto.ai" className="h-8 w-auto" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Products
                </p>
                {productItems.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-product-${l.to.slice(1)}`}
                      className={({ isActive }) =>
                        `rounded-lg px-3 py-3 text-base font-medium transition-colors ${
                          isActive ? "bg-brand-50 text-brand" : "text-slate-700 hover:bg-slate-50"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
                <div className="my-2 h-px bg-slate-100" />
                <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Demo
                </p>
                {demoItems.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-demo-${l.to.split("/").pop()}`}
                      className={({ isActive }) =>
                        `rounded-lg px-3 py-3 text-base font-medium transition-colors ${
                          isActive ? "bg-brand-50 text-brand" : "text-slate-700 hover:bg-slate-50"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
                <div className="my-2 h-px bg-slate-100" />
                <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Tools
                </p>
                {toolItems.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-tool-${l.to.split("/").pop()}`}
                      className={({ isActive }) =>
                        `rounded-lg px-3 py-3 text-base font-medium transition-colors ${
                          isActive ? "bg-brand-50 text-brand" : "text-slate-700 hover:bg-slate-50"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
                <div className="my-2 h-px bg-slate-100" />
                <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Resources
                </p>
                {resourceItems.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-resource-${l.to.split("/").pop()}`}
                      className={({ isActive }) =>
                        `rounded-lg px-3 py-3 text-base font-medium transition-colors ${
                          isActive ? "bg-brand-50 text-brand" : "text-slate-700 hover:bg-slate-50"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
                <div className="my-2 h-px bg-slate-100" />
                {navLinks.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-${l.testid}`}
                      className={({ isActive }) =>
                        `rounded-lg px-3 py-3 text-base font-medium transition-colors ${
                          isActive ? "bg-brand-50 text-brand" : "text-slate-700 hover:bg-slate-50"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
              </div>
              <SheetClose asChild>
                <Button
                  data-testid="mobile-book-demo-btn"
                  onClick={() => {
                    trackButton("Book Demo", { source: "mobile_header" });
                    navigate("/contact-sales");
                  }}
                  className="mt-6 w-full rounded-full bg-brand font-semibold text-white hover:bg-brand-600"
                >
                  Book a Demo
                </Button>
              </SheetClose>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
